import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { CalendarIcon, NewspaperIcon, ArchiveIcon, ChevronDownIcon } from '../ui/Icons';

// Accordion Item Component
const AccordionItem = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border border-glass-border rounded-xl mb-3 overflow-hidden transition-all hover:border-accent-gold/50">
      <button
        className="w-full px-5 py-4 flex items-center justify-between text-left bg-glass-bg hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex-1 pr-4">
          <h4 className="text-white font-medium line-clamp-2">{item.Title || item.currentAffairsHead}</h4>
          <p className="text-text-muted text-sm mt-1 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            {new Date(item.DateEvent).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long', // 'short' for Oct, 'numeric' for 10
              day: 'numeric'
            })}
          </p>
        </div>
        <ChevronDownIcon className={`w-5 h-5 text-accent-gold transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="px-5 py-4 border-t border-glass-border bg-white/5 animate-slide-down">
          <div 
            className="text-text-light leading-relaxed prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: item.currentAffairsContent || item.currentAffairsHead }}
          />
          {item.category && (
            <span className="inline-block mt-3 px-3 py-1 bg-primary-purple/30 text-accent-gold text-xs rounded-full">
              {item.currentAffairsHead}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Archive Item Component
const ArchiveItem = ({ archive }) => {
  return (
    <Link
      to={`/current-affairs?range=${encodeURIComponent(archive.dateRange)}`}
      className="block p-4 border border-glass-border rounded-xl hover:border-accent-gold hover:bg-white/5 transition-all group"
    >
      <div className="flex items-center gap-3">
        <CalendarIcon className="w-5 h-5 text-accent-gold" />
        <div>
          <p className="text-white group-hover:text-accent-gold transition-colors font-medium">
            {archive.dateRange}
          </p>
          <p className="text-text-muted text-sm">{archive.count} articles</p>
        </div>
      </div>
    </Link>
  );
};

const CurrentAffairsSection = () => {
  const [affairs, setAffairs] = useState([]);
  const [archives, setArchives] = useState([]);
  const [currentDateRange, setCurrentDateRange] = useState('');
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch latest current affairs
        const latestResponse = await api.get('/current-affairs/latest');
        if (latestResponse.data.success) {
          setAffairs(latestResponse.data.data);
          setCurrentDateRange(latestResponse.data.dateRange);
        }

        // Fetch archives
        const archivesResponse = await api.get('/current-affairs/archives');
        if (archivesResponse.data.success) {
          // Filter out current date range from archives
          const filteredArchives = archivesResponse.data.data.filter(
            a => a.dateRange !== latestResponse.data.dateRange
          );
          setArchives(filteredArchives.slice(0, 10)); // Show only 10 archives
        }
      } catch (error) {
        console.error('Error fetching current affairs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-dark-purple/20 to-secondary-purple/20">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center">
            <div className="animate-spin text-4xl mb-4">⏳</div>
            <p className="text-text-muted">Loading current affairs...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-dark-purple/20 to-secondary-purple/20" id="current-affairs">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Current Affairs</h2>
          <p className="section-subtitle">
            Stay updated with the latest news and events for your exam preparation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Latest Current Affairs (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="glass-card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-3">
                  <NewspaperIcon className="w-6 h-6 text-accent-gold" />
                  Latest Updates
                </h3>
                {currentDateRange && (
                  <span className="px-4 py-2 bg-accent-gold/20 text-accent-gold rounded-full text-sm font-medium">
                    {currentDateRange}
                  </span>
                )}
              </div>

              {affairs.length === 0 ? (
                <div className="text-center py-12">
                  <NewspaperIcon className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-muted">No current affairs available at the moment.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {affairs.slice(0, 8).map((item) => (
                    <AccordionItem
                      key={item.currentAffairsId}
                      item={item}
                      isOpen={openAccordion === item.currentAffairsId}
                      onToggle={() => toggleAccordion(item.currentAffairsId)}
                    />
                  ))}
                </div>
              )}

              {affairs.length > 8 && (
                <div className="mt-6 text-center">
                  <Link 
                    to="/current-affairs" 
                    className="text-accent-gold hover:underline font-medium"
                  >
                    View all {affairs.length} articles →
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Archives (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="glass-card sticky top-28">
              <h3 className="text-xl font-bold text-white flex items-center gap-3 mb-6">
                <ArchiveIcon className="w-6 h-6 text-accent-gold" />
                Archives
              </h3>

              {archives.length === 0 ? (
                <div className="text-center py-8">
                  <ArchiveIcon className="w-10 h-10 text-text-muted mx-auto mb-3" />
                  <p className="text-text-muted text-sm">No archives available.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {archives.map((archive, index) => (
                    <ArchiveItem key={index} archive={archive} />
                  ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-glass-border">
                <Link 
                  to="/current-affairs" 
                  className="btn-secondary w-full text-center block"
                >
                  View All Archives
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CurrentAffairsSection;